/**
 * ## Ours (Base UI)
 * Animated open/close, arrow, viewport with scroll, `side` / `align` / `sideOffset` / `alignOffset` props. Optional `tooltipStyle` mode on `PopoverPopup`.
 * * `PopoverPopup` / `PopoverContent` — positioner + popup + arrow + viewport; wraps children
 * * `PopoverClose` — close button
 * * `PopoverTitle` — popup title
 * * `PopoverDescription` — popup description
 * * `PopoverCreateHandle` — `PopoverPrimitive.createHandle` re-export
 *
 * ## Shadcn UI
 * Basic popover, no arrow, no viewport.
 * * `PopoverContent` — content; `align` / `sideOffset` props
 * * `PopoverAnchor` — decoupled anchor (no equivalent exported, use `anchor` prop on `PopoverPrimitive.Positioner` instead)
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */

'use client'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'
import { cn } from '../lib/utils'
import { ArrowSvg } from './arrow-svg'

// INFO: PopoverPrimitive.Backdrop is unused.

const PopoverCreateHandle = PopoverPrimitive.createHandle
const Popover = PopoverPrimitive.Root

function PopoverTrigger(props: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

// INFO: Coss UI decision — Positioner, Popup, Arrow, and Viewport are intentionally collapsed into
// this single component, with positioning props surfaced here.
function PopoverPopup({
  anchor,
  children,
  className,
  side = 'bottom',
  align = 'center',
  sideOffset = 4,
  alignOffset = 0,
  tooltipStyle = false,
  showArrow = true,
  positionerClassName,
  ...props
}: PopoverPrimitive.Popup.Props & {
  anchor?: PopoverPrimitive.Positioner.Props['anchor']
  side?: PopoverPrimitive.Positioner.Props['side']
  align?: PopoverPrimitive.Positioner.Props['align']
  sideOffset?: PopoverPrimitive.Positioner.Props['sideOffset']
  alignOffset?: PopoverPrimitive.Positioner.Props['alignOffset']
  tooltipStyle?: boolean
  showArrow?: boolean
  positionerClassName?: string
}) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        anchor={anchor}
        align={align}
        alignOffset={alignOffset}
        className={cn('z-50 h-(--positioner-height) w-(--positioner-width) max-w-(--available-width) transition-[top,left,right,bottom,transform] data-instant:transition-none', positionerClassName)}
        data-slot="popover-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <PopoverPrimitive.Popup
          className={cn(
            'bg-surface-3 text-on-surface relative flex h-(--popup-height,auto) w-(--popup-width,auto) origin-(--transform-origin) rounded-[12px] transition-[width,height,scale,opacity] duration-150 ease-out data-ending-style:scale-98 data-ending-style:opacity-0 data-starting-style:scale-98 data-starting-style:opacity-0',
            tooltipStyle && 'w-fit rounded-md text-xs text-balance',
            className,
          )}
          data-slot="popover-popup"
          {...props}
        >
          {showArrow ? (
            <PopoverPrimitive.Arrow className="flex data-[side=bottom]:top-[-8px] data-[side=bottom]:rotate-0 data-[side=left]:right-[-13px] data-[side=left]:rotate-90 data-[side=right]:left-[-12.1px] data-[side=right]:-rotate-90 data-[side=top]:bottom-[-7.2px] data-[side=top]:rotate-180">
              <ArrowSvg />
            </PopoverPrimitive.Arrow>
          ) : null}
          <PopoverPrimitive.Viewport
            className={cn(
              'relative size-full max-h-(--available-height) overflow-clip px-(--viewport-inline-padding) py-4 outline-none [--viewport-inline-padding:--spacing(4)] **:data-current:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-current:opacity-100 **:data-current:transition-opacity **:data-current:data-ending-style:opacity-0 data-instant:transition-none **:data-previous:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-previous:opacity-100 **:data-previous:transition-opacity **:data-previous:data-ending-style:opacity-0 **:data-current:data-starting-style:opacity-0 **:data-previous:data-starting-style:opacity-0',
              tooltipStyle
                ? 'py-1 [--viewport-inline-padding:--spacing(2)]'
                : 'not-data-transitioning:overflow-y-auto',
            )}
            data-slot="popover-viewport"
          >
            {children}
          </PopoverPrimitive.Viewport>
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

function PopoverClose({ ...props }: PopoverPrimitive.Close.Props) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      className={cn('text-lg leading-none font-semibold', className)}
      data-slot="popover-title"
      {...props}
    />
  )
}

function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      className={cn('text-on-surface-secondary text-sm', className)}
      data-slot="popover-description"
      {...props}
    />
  )
}

export {
  PopoverCreateHandle,
  Popover,
  PopoverTrigger,
  PopoverPopup,
  PopoverPopup as PopoverContent,
  PopoverTitle,
  PopoverDescription,
  PopoverClose,
}
